import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Modal, Field, Select, Badge, Empty } from "@/components/Kit";
import { useYearOptions } from "@/lib/academicYear";
import { fmtDate } from "@/lib/date";
import { Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = { name: "", exam_type: "FA", academic_year: "", max_marks: 50, start_date: "", end_date: "", order: 0 };
const STATUS = { scheduled: "#06B6D4", marks_entered: "#10B981" };

export default function ExamsTab() {
  const qc = useQueryClient();
  const { years: YEARS, current } = useYearOptions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, academic_year: current });
  const { data: exams = [] } = useQuery({ queryKey: ["exams"], queryFn: async () => (await api.get("/school/academics/exams")).data });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, max_marks: Number(form.max_marks), order: Number(form.order) || 0, classes: [] };
      return editing ? (await api.put(`/school/academics/exams/${editing.id}`, body)).data : (await api.post("/school/academics/exams", body)).data;
    },
    onSuccess: () => { toast.success("Exam saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["exams"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/school/academics/exams/${id}`)).data,
    onSuccess: () => { toast.success("Exam removed"); qc.invalidateQueries({ queryKey: ["exams"] }); },
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="exams-tab">
      <div className="mb-4 flex justify-end"><button className="ep-btn" onClick={() => { setEditing(null); setForm({ ...EMPTY, academic_year: current, order: exams.length }); setOpen(true); }} data-testid="add-exam-btn"><Plus size={16} /> Schedule Exam</button></div>
      <div className="ep-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Exam</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Year</th><th className="px-4 py-3 text-right">Max</th><th className="px-4 py-3">Dates</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {exams.map((e) => (
              <tr key={e.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`exam-row-${e.name}`}>
                <td className="px-4 py-3 font-semibold text-white">{e.name}</td>
                <td className="px-4 py-3"><Badge color={e.exam_type === "SA" ? "#F59E0B" : "#4F46E5"}>{e.exam_type === "SA" ? "Summative" : "Formative"}</Badge></td>
                <td className="px-4 py-3 text-slate-400">{e.academic_year}</td>
                <td className="px-4 py-3 text-right text-slate-300">{e.max_marks}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(e.start_date)} → {fmtDate(e.end_date)}</td>
                <td className="px-4 py-3"><Badge color={STATUS[e.status] || "#94A3B8"}>{(e.status || "scheduled").replace("_", " ")}</Badge></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1.5">
                  <button onClick={() => { setEditing(e); setForm(e); setOpen(true); }} className="rounded-lg bg-slate-700/40 p-2 text-slate-300 hover:bg-slate-700/70" data-testid={`edit-exam-${e.name}`}><Pencil size={14} /></button>
                  <button onClick={() => remove.mutate(e.id)} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" data-testid={`del-exam-${e.name}`}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
            {!exams.length && <tr><td colSpan={7}><Empty>No exams scheduled</Empty></td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Exam" : "Schedule Exam"} testid="exam-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Exam Name *" value={form.name} onChange={upd("name")} placeholder="FA1 / SA1" data-testid="ex-name" />
          <Select label="Type" value={form.exam_type} onChange={upd("exam_type")} data-testid="ex-type"><option value="FA">Formative (FA)</option><option value="SA">Summative (SA)</option></Select>
          <Select label="Academic Year" value={form.academic_year} onChange={upd("academic_year")} data-testid="ex-year">{YEARS.map((y) => <option key={y}>{y}</option>)}</Select>
          <Field label="Max Marks / Subject" type="number" value={form.max_marks} onChange={upd("max_marks")} data-testid="ex-max" />
          <Field label="Start Date" type="date" value={form.start_date} onChange={upd("start_date")} data-testid="ex-start" />
          <Field label="End Date" type="date" value={form.end_date} onChange={upd("end_date")} data-testid="ex-end" />
        </div>
        <button className="ep-btn mt-5 w-full" disabled={save.isPending || !form.name} onClick={() => save.mutate()} data-testid="save-exam-btn">{save.isPending ? "Saving…" : "Save Exam"}</button>
      </Modal>
    </div>
  );
}
